import { useNavigate } from "react-router-dom";
import { useMobile } from "../../context/MobileContext";
import { clientOverviewPath, clientPortalPath } from "../../nav/consultantNav";
import { theme } from "../../theme";
import { useConsultantShell } from "./ConsultantShellContext";

// ── Client switcher ───────────────────────────────────────────────────────
// A small select in the top bar of every client-scoped route. Fed by the
// light GET /consultant/clients list; picking a client keeps the current
// mode (management view or read-through).

export function ClientSwitcher({ orgId, currentName, mode }: {
  orgId: string;
  /** Name to show when the organisation is not in the firm's list (platform owner scoping another firm's client). */
  currentName?: string | null;
  mode: "overview" | "portal";
}) {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { clients, clientsLoaded } = useConsultantShell();
  const known = clients.some((c) => c.id === orgId);

  return (
    <select
      aria-label="Switch client"
      value={orgId}
      onChange={(e) => {
        const id = e.target.value;
        if (!id || id === orgId) return;
        navigate(mode === "portal" ? clientPortalPath(id) : clientOverviewPath(id));
      }}
      style={{
        background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`,
        borderRadius: 3, padding: "4px 8px", fontSize: 12, color: theme.color.text,
        cursor: "pointer", outline: "none", fontFamily: theme.font,
        // Shares the top-bar slot with the breadcrumb: it may shrink (never
        // below its own text on desktop) and never wins the whole slot.
        minWidth: 0, maxWidth: 220, flex: isMobile ? "0 1 120px" : "0 1 160px",
      }}
    >
      {!known && (
        <option value={orgId} disabled>
          {currentName ?? (clientsLoaded ? "Current client" : "Loading clients")}
        </option>
      )}
      {clients.map((c) => (
        <option key={c.id} value={c.id}>{c.name}</option>
      ))}
    </select>
  );
}
